import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CurrencyDollar, FileText, Flask, LinkSimple, X } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";
import { SkuMappingDialog } from "./PartnerSkuMap";
import { PayoutStatementDialog } from "./PayoutStatementDialog";

const fmt$ = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const PILL = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  unmatched: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
  expired: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const shipLine = (s) =>
  s ? [s.ship_name, s.ship_line1, s.ship_line2, s.ship_city, s.ship_province, s.ship_postal_code]
      .filter(Boolean).join(", ") : "";

/** v410.1 — Active Bio Labs partner orders inside the Store Orders console:
 *  fulfillment list, record-an-e-Transfer (runs the matcher), and unmatched
 *  payments review with match-by-hand. */
export function PartnerOrdersSection() {
  const [orders, setOrders] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransfer, setShowTransfer] = useState(false);
  const [tf, setTf] = useState({ sender_email: "", amount_cad: "", reference: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveRef, setResolveRef] = useState("");
  const [showSkuMap, setShowSkuMap] = useState(false);
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [showStatement, setShowStatement] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  const [loadError, setLoadError] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, u] = await Promise.all([
        api.get("/partner-admin/orders"),
        api.get("/partner-admin/unmatched"),
      ]);
      setOrders(o.data.orders || []);
      setUnmatched(u.data.unmatched || []);
      setLoadError(false);
      setAuthorized(true);
      // v414.57 — badge the SKU-mapping button with SKUs that still need a map
      api.get("/partner-admin/unmapped-skus")
        .then(({ data }) => setUnmappedCount((data.unmapped || []).length))
        .catch(() => {});
    } catch (e) {
      // 403 = not a partner admin (section simply stays hidden);
      // anything else deserves a visible hint instead of a silent void.
      if (e?.response?.status !== 403) setLoadError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const recordTransfer = async () => {
    if (!tf.sender_email || !tf.amount_cad || !tf.reference) {
      toast.error("Sender email, amount and Interac reference are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/partner-admin/etransfers", {
        sender_email: tf.sender_email, amount_cad: parseFloat(tf.amount_cad),
        reference: tf.reference, message: tf.message,
      });
      if (data.outcome === "matched") {
        toast.success(`Matched! Order ${data.matched_order_ref} is now PAID.`);
      } else if (data.outcome === "duplicate") {
        toast.info("This transfer reference was already recorded.");
      } else {
        toast.warning("No automatic match — parked in Unmatched payments for review.");
      }
      setShowTransfer(false);
      setTf({ sender_email: "", amount_cad: "", reference: "", message: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resolvePayment = async () => {
    if (!resolveRef.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-admin/unmatched/${resolveTarget.id}/resolve`,
        { order_ref: resolveRef.trim() });
      toast.success(`Matched to ${data.order_ref} — order is now PAID.`);
      setResolveTarget(null);
      setResolveRef("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dismissPayment = async (p) => {
    if (!window.confirm(
      `Dismiss the ${fmt$(p.amount_cad)} payment from ${p.email}?\n\nUse this when the money already paid its order (duplicate notification). No order or stock is touched — it only clears this card.`)) return;
    setBusy(true);
    try {
      await api.post(`/partner-admin/unmatched/${p.id}/dismiss`);
      toast.success("Payment dismissed — no order or stock was changed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (o) => {
    const ref = window.prompt(
      `Mark ${o.order_ref} (${fmt$(o.total_cad)}) as PAID?\n\nOptional: paste the Interac reference number (leave blank if you don't have it), then press OK.`,
      "");
    if (ref === null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-admin/orders/${o.order_ref}/mark-paid`, { reference: ref || "" });
      toast.success(
        data.alert_sent
          ? `${o.order_ref} marked PAID — stock deducted, Store notified, paid alert emailed.`
          : `${o.order_ref} marked PAID — stock deducted, Store notified. Paid alert email FAILED — check email settings.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revertPaid = async (o) => {
    if (!window.confirm(`Revert ${o.order_ref} to UNPAID? Stock goes back on reserve and a DO-NOT-SHIP alert is emailed.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-admin/orders/${o.order_ref}/revert-paid`,
        { reason: "reverted from Store Orders console" });
      toast.success(`${data.order_ref} is back to pending (${data.units_restored} unit(s) restored to reserve)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!loading && !loadError && !authorized) return null;

  if (loadError) {
    return (
      <div className="pt-6 border-t-2 border-zinc-200" data-testid="partner-orders-error">
        <p className="text-xs text-zinc-500">
          Active Bio Labs orders couldn&apos;t load.{" "}
          <button onClick={load} className="underline font-medium">Retry</button>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-6 border-t-2 border-zinc-200" data-testid="partner-orders-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Flask size={20} weight="fill" className="text-[#8a6a1c]" />
          <h2 className="text-base md:text-lg font-semibold text-zinc-950">Active Bio Labs orders</h2>
          <span className="text-[11px] font-medium text-zinc-400">{orders.length}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <BrutalButton variant="outline" size="sm" onClick={() => setShowStatement(true)} data-testid="payout-statement-btn">
            <FileText size={15} weight="bold" /> Payout statement
          </BrutalButton>
          <BrutalButton variant="outline" size="sm" onClick={() => setShowSkuMap(true)} data-testid="sku-map-btn">
            <LinkSimple size={15} weight="bold" /> SKU mapping
            {unmappedCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold"
                data-testid="sku-map-badge">
                {unmappedCount}
              </span>
            )}
          </BrutalButton>
          <BrutalButton variant="dark" size="sm" onClick={() => setShowTransfer(true)} data-testid="record-etransfer-btn">
            <CurrencyDollar size={15} weight="bold" /> Record e-Transfer
          </BrutalButton>
        </div>
      </div>

      {unmatched.length > 0 && (
        <div className="border-2 border-red-300 bg-red-50/60 rounded-xl p-4 space-y-2" data-testid="unmatched-payments-block">
          <p className="text-sm font-semibold text-red-800">
            Unmatched payments — money arrived, needs a human ({unmatched.length})
          </p>
          {unmatched.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-2 text-sm bg-white border border-red-200 rounded-lg px-3 py-2" data-testid={`unmatched-${p.id}`}>
              <span className="font-semibold tabular-nums">{fmt$(p.amount_cad)}</span>
              <span className="text-zinc-600">{p.email}</span>
              <span className="text-[11px] text-zinc-400">ref {p.reference}</span>
              {p.candidate_order_ref && (
                <span className="text-[11px] text-amber-700">closest: {p.candidate_order_ref}</span>
              )}
              <BrutalButton variant="outline" size="sm" className="ml-auto"
                onClick={() => { setResolveTarget(p); setResolveRef(p.candidate_order_ref || ""); }}
                data-testid={`resolve-${p.id}`}>
                Match to order…
              </BrutalButton>
              <BrutalButton variant="secondary" size="sm" disabled={busy}
                onClick={() => dismissPayment(p)}
                title="Duplicate / already paid — clears this card without touching any order or stock"
                data-testid={`dismiss-${p.id}`}>
                Dismiss
              </BrutalButton>
            </div>
          ))}
        </div>
      )}

      <div className="border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100 bg-white">
        {loading ? (
          <p className="p-4 text-sm text-zinc-500">Loading…</p>
        ) : orders.length === 0 ? (
          <p className="p-4 text-sm text-zinc-400" data-testid="partner-orders-empty">
            No Active Bio Labs orders yet — they'll appear here the moment the Store sends one.
          </p>
        ) : orders.map((o) => (
          <div key={o.id} className="p-3 sm:p-4 space-y-1" data-testid={`partner-order-${o.order_ref}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-sm tracking-wide text-[#8a6a1c]">{o.order_ref}</span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 border rounded-full ${PILL[o.status] || PILL.cancelled}`}>
                {o.status}
              </span>
              <span className="text-xs text-zinc-400">{fmtDate(o.created_at)}</span>
              <span className="ml-auto text-right">
                <span className="block font-semibold text-sm tabular-nums">{fmt$(o.total_cad)}</span>
                {Number(o.shipping_cad) > 0 && (
                  <span className="block text-[10px] text-zinc-400 tabular-nums" data-testid={`po-split-${o.order_ref}`}>
                    products {fmt$((o.total_cad || 0) - (o.shipping_cad || 0))} · shipping {fmt$(o.shipping_cad)}
                  </span>
                )}
                {(() => {
                  const itemsSum = (o.items || []).reduce((s, i) => s + Number(i.total_cad || 0), 0);
                  const productsNet = (o.total_cad || 0) - (o.shipping_cad || 0);
                  const disc = itemsSum - productsNet;
                  return disc > 0.009 ? (
                    <span className="block text-[10px] text-emerald-700 font-semibold tabular-nums" data-testid={`po-discount-${o.order_ref}`}>
                      after {fmt$(disc)} store discount
                    </span>
                  ) : null;
                })()}
              </span>
            </div>
            <p className="text-xs text-zinc-600">
              {o.name || "—"}{o.email ? ` · ${o.email}` : ""} · {(o.items || []).map((i) => `${i.qty}× ${i.name || i.sku}`).join(", ")}
            </p>
            {o.status === "paid" && shipLine(o.shipping) && (
              <p className="text-xs text-zinc-800 bg-[#fbf6e7] border border-[#c9a24a]/50 rounded px-2 py-1 inline-block">
                SHIP TO: {shipLine(o.shipping)}
              </p>
            )}
            {(o.status === "pending" || o.status === "unmatched") && (
              <BrutalButton size="sm" variant="dark" disabled={busy}
                onClick={() => markPaid(o)} data-testid={`mark-paid-${o.order_ref}`}>
                Mark as paid
              </BrutalButton>
            )}
            {o.status === "paid" && (
              <button onClick={() => revertPaid(o)} disabled={busy}
                className="block text-[11px] text-red-500 hover:text-red-700 underline"
                data-testid={`revert-paid-${o.order_ref}`}>
                Revert to unpaid (payment was a mistake)
              </button>
            )}
          </div>
        ))}
      </div>

      {showSkuMap && (
        <SkuMappingDialog onClose={() => setShowSkuMap(false)} onChanged={load} />
      )}
      {showStatement && (
        <PayoutStatementDialog onClose={() => setShowStatement(false)} />
      )}

      {showTransfer && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowTransfer(false)}>
          <div className="bg-white rounded-xl border-2 border-zinc-900 w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="etransfer-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Record incoming e-Transfer</h3>
              <button onClick={() => setShowTransfer(false)} className="text-zinc-400 hover:text-zinc-900"><X size={18} weight="bold" /></button>
            </div>
            <p className="text-xs text-zinc-500">Enter it exactly as it appears in the Interac email. If it lines up with an order, the order is marked paid automatically.</p>
            <BrutalInput placeholder="Sender email" value={tf.sender_email}
              onChange={(e) => setTf({ ...tf, sender_email: e.target.value })} data-testid="tf-email" />
            <BrutalInput placeholder="Amount (CAD)" type="number" step="0.01" value={tf.amount_cad}
              onChange={(e) => setTf({ ...tf, amount_cad: e.target.value })} data-testid="tf-amount" />
            <BrutalInput placeholder="Interac reference number" value={tf.reference}
              onChange={(e) => setTf({ ...tf, reference: e.target.value })} data-testid="tf-reference" />
            <BrutalInput placeholder="Message on the transfer (e.g. ABL-ORD-…)" value={tf.message}
              onChange={(e) => setTf({ ...tf, message: e.target.value })} data-testid="tf-message" />
            <BrutalButton variant="dark" className="w-full" onClick={recordTransfer} disabled={busy} data-testid="tf-submit">
              {busy ? "Matching…" : "Record & match"}
            </BrutalButton>
          </div>
        </div>
      )}

      {resolveTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setResolveTarget(null)}>
          <div className="bg-white rounded-xl border-2 border-zinc-900 w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="resolve-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Match {fmt$(resolveTarget.amount_cad)} to an order</h3>
              <button onClick={() => setResolveTarget(null)} className="text-zinc-400 hover:text-zinc-900"><X size={18} weight="bold" /></button>
            </div>
            <p className="text-xs text-zinc-500">Type the order reference this payment belongs to. The order will be marked PAID and stock deducted.</p>
            <BrutalInput placeholder="ABL-ORD-…" value={resolveRef}
              onChange={(e) => setResolveRef(e.target.value)} data-testid="resolve-ref-input" />
            <BrutalButton variant="dark" className="w-full" onClick={resolvePayment} disabled={busy} data-testid="resolve-submit">
              {busy ? "Matching…" : "Confirm match"}
            </BrutalButton>
          </div>
        </div>
      )}

      <button onClick={load} className="text-xs text-zinc-400 hover:text-zinc-700 inline-flex items-center gap-1" data-testid="partner-refresh">
        <ArrowsClockwise size={12} weight="bold" /> Refresh
      </button>
    </div>
  );
}
